import type { NodePropertySchema } from '../../../types/node-properties';

const schema: NodePropertySchema = {
  nodeType: 'agentOutputParser',
  version: '1.1.0',
  properties: [
    // ==================================================================
    // SCHEMA DEFINITION
    // ==================================================================
    {
      key: 'expectedOutputType',
      label: 'Expected Output Type',
      type: 'hidden',
      required: false,
      default: 'either',
      description: 'Auto-detected from JSON Example',
    },
    {
      key: 'jsonExample',
      label: 'JSON Example',
      type: 'jsonSchemaEditor',
      required: true,
      default: '{\n  "name": "John Doe",\n  "age": 30,\n  "email": "john@example.com"\n}',
      placeholder: 'Enter example JSON structure',
      description: 'Provide an example of the JSON structure you want. The schema will be auto-generated.',
    },
    {
      key: 'schemaDescription',
      label: 'Schema Description',
      type: 'string',
      required: false,
      default: '',
      placeholder: 'Describe what this schema represents (helps the AI understand context)',
      description: 'Optional description to help the model understand the schema purpose',
      hint: 'Example: "Extract customer contact information from support tickets"',
      typeOptions: {
        rows: 2,
      },
    },

    // ==================================================================
    // VALIDATION OPTIONS
    // ==================================================================
    {
      key: 'strictValidation',
      label: 'Strict Validation',
      type: 'boolean',
      required: false,
      default: true,
      description: 'Enforce strict schema validation (reject additional properties)',
      hint: 'When enabled, the AI output must exactly match the schema structure',
    },
    {
      key: 'enableAutoFix',
      label: 'Enable Auto-Fix',
      type: 'boolean',
      required: false,
      default: true,
      description: 'Attempt to automatically fix common JSON formatting errors',
      hint: 'Fixes issues like markdown code blocks, trailing commas, smart quotes',
    },

    // ==================================================================
    // RETRY CONFIGURATION
    // ==================================================================
    {
      key: 'enableRetry',
      label: 'Enable Retry',
      type: 'boolean',
      required: false,
      default: true,
      description: 'Retry with LLM if validation fails',
      hint: 'When enabled, invalid outputs are sent back to the AI for correction',
    },
    {
      key: 'maxRetries',
      label: 'Max Retries',
      type: 'number',
      required: false,
      default: 3,
      typeOptions: {
        minValue: 1,
        maxValue: 10,
      },
      description: 'Maximum number of retry attempts',
      displayOptions: {
        show: {
          enableRetry: [true],
        },
      },
    },
    {
      key: 'retryPromptTemplate',
      label: 'Retry Prompt Template',
      type: 'string',
      required: false,
      default: 'The previous output did not match the required format.\n\nRequired format:\n{instructions}\n\nYour previous output:\n{completion}\n\nError:\n{error}\n\nPlease provide the output again in the correct JSON format.',
      placeholder: 'Use {instructions}, {completion}, {error} as variables',
      description: 'Template for retry prompts. Available variables: {instructions}, {completion}, {error}',
      typeOptions: {
        rows: 6,
      },
      displayOptions: {
        show: {
          enableRetry: [true],
        },
      },
    },

    // ==================================================================
    // ERROR HANDLING
    // ==================================================================
    {
      key: 'onValidationFailure',
      label: 'On Validation Failure',
      type: 'options',
      required: false,
      default: 'error',
      options: [
        { value: 'error', name: 'Throw Error' },
        { value: 'partial', name: 'Return Partial Data with Warnings' },
        { value: 'raw', name: 'Return Raw Output' },
      ],
      description: 'What to do if validation fails after all retries',
    },

    // ==================================================================
    // OUTPUT OPTIONS
    // ==================================================================
    {
      key: 'includeMetadata',
      label: 'Include Metadata',
      type: 'boolean',
      required: false,
      default: true,
      description: 'Include validation metadata in output (retries, fixes, warnings)',
    },
  ],
};

export default schema;
